import { useDispatch, useSelector } from '@redux';
import crudActions from '@redux/crudActions';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import CustomBreadcrumb from 'components/commons/Breadcrumb';
import EditActions from 'components/rest/EditActions';
import RestForm from 'components/rest/RestForm';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DynamicObjectType } from '../../../types/commons';
import crudSelectors from '@redux/crudSelectors';
import ButtonRow from 'components/rest/FooterButtonRow';
import { getIdByUrl } from 'utils/tools';
import { useRootPath } from 'hooks/useLayout';
import _, { zipObjectDeep } from 'lodash';

export interface IRestCreate {
  breadcrumbs?: ItemType[];
  children?: React.ReactNode;
  model: string;
  id?: string;
  // options?: { label; value }[];
  options?: React.ReactNode[];
  onSelect?: (value) => void;
  backOnSuccess?: boolean;
  formatOnSubmit?: (
    values: DynamicObjectType,
    record?: DynamicObjectType,
  ) => void;
  apiOptions?: DynamicObjectType;
  // eslint-disable-next-line
  customApi?: any;
  customSubmit?: (form) => void;
  handleDataConvert?: (record) => void;
  showModal?: boolean;
  customUrlBack?: string;
  handleSubmitFailed?: (values: DynamicObjectType) => void;
  hiddenLayoutTop?: boolean;
  customActionEdit?: (
    values: DynamicObjectType,
    record?: DynamicObjectType,
  ) => void;
  saveButtonLabel?: string;
  isOpenModal?: boolean;
  titleModal?: string;
  descriptionModal?: string;
  customPathAfterSubmit?: string;
}

function RestEditContent({
  breadcrumbs,
  children,
  model,
  id,
  options,
  onSelect,
  apiOptions,
  customApi,
  customSubmit,
  showModal,
  customUrlBack,
  hiddenLayoutTop,
  saveButtonLabel = 'Save',
  isOpenModal = false,
  titleModal,
  descriptionModal,
}: IRestCreate) {
  const dispatch = useDispatch();

  const defaultBreadcrumbs = useMemo(
    () =>
      breadcrumbs || [
        {
          title: `${model}.listTitle`,
          href: `/${model}`,
        },
        {
          title: `${model}.editTitle`,
        },
      ],
    [breadcrumbs, model],
  );

  useEffect(() => {
    if (id) {
      if (customApi) {
        dispatch(
          customApi({
            data: {
              id,
            },
            options: {
              ...apiOptions,
            },
          }),
        );
      } else {
        dispatch(
          crudActions[model].getDataById({
            data: { id },
            options: {
              isRequestApi: true,
              ...apiOptions,
            },
          }),
        );
      }
    }
  }, [id]);

  return (
    <>
      {showModal ? (
        <>
          <div className="showModal-content">{children}</div>
          <ButtonRow model={model} type="edit" />
        </>
      ) : (
        <>
          {!hiddenLayoutTop && (
            <div className="d-flex space-between action-edit gap-[15px]">
              <CustomBreadcrumb items={defaultBreadcrumbs} />
              <EditActions
                model={model}
                id={id}
                options={options}
                onSelect={onSelect}
                customSubmit={customSubmit}
                customUrlBack={customUrlBack}
                saveButtonLabel={saveButtonLabel}
                isOpenModal={isOpenModal}
                titleModal={titleModal}
                descriptionModal={descriptionModal}
              />
            </div>
          )}
          {children}
        </>
      )}
    </>
  );
}

export default ({
  backOnSuccess,
  id,
  customSubmit,
  handleDataConvert,
  handleSubmitFailed,
  customActionEdit,
  ...props
}: IRestCreate) => {
  const record = useSelector(
    crudSelectors[props.model].getCurrentData,
  ) as DynamicObjectType;
  const rootPath = useRootPath();

  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const _id = useMemo(
    () => id || getIdByUrl(props, location) || params.id,
    [params.id, id],
  );
  const navigate = useNavigate();
  const handleEdit = (values: DynamicObjectType) => {
    const rawPayload = { id: _id, ...values };
    const parseToObj = zipObjectDeep(
      Object.keys(rawPayload),
      _.values(rawPayload),
    );
    if (customActionEdit) {
      customActionEdit(parseToObj, record);
    } else {
      dispatch(
        crudActions[props.model].edit({
          data: props.formatOnSubmit
            ? props.formatOnSubmit(parseToObj, record)
            : parseToObj,
          options: {
            isShowSuccessNoti: true,
          },
        }),
      ).then(({ payload: { data } }) => {
        if (props.customPathAfterSubmit) {
          navigate(`/${props?.customPathAfterSubmit}`);
        }
        props.isOpenModal &&
          data?.id &&
          dispatch(
            crudActions.modal.toogleModal({
              ['confirm']: {
                isOpen: false,
              },
            } as any),
          );
        backOnSuccess && data?.id && navigate(`/${rootPath}`);
      });
    }
  };

  return (
    <RestForm
      handleSubmitFailed={handleSubmitFailed}
      onSubmit={handleEdit}
      record={record}
      handleDataConvert={handleDataConvert}
      isEdit
    >
      <RestEditContent id={_id} customSubmit={customSubmit} {...props} />
    </RestForm>
  );
};
